"use client";

import React, { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowUpRight, Compass, Map, Book, Users, Phone, Instagram, Menu, X } from "lucide-react";
import { usePathname } from "next/navigation";

const navItems = [
  { text: "Start Here", href: "/", icon: Compass },
  { text: "Pick an Outing", href: "/trails", icon: Map },
  { text: "Blogs", href: "/blog", icon: Book },
  {
    text: "About Us",
    href: "/AboutUs/our-story",
    icon: Users,
    hasDropdown: true,
    dropdownItems: [
      { text: "Our Story", href: "/AboutUs/our-story" },
      { text: "Trip Terms & Condition", href: "/AboutUs/terms-condition" },
    ],
  },
  { text: "Let's Talk", href: "/contact", icon: Phone },
];

const Header = () => {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setMobileDropdownOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll(); // Check immediately on load/refresh
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (item) =>
    item.href === pathname ||
    (item.hasDropdown && item.dropdownItems.some((sub) => sub.href === pathname));

  return (
    <>
      <motion.header
        className="fixed top-0 left-0 right-0 z-[100] transition-none pointer-events-none"
        animate={{
          paddingTop: scrolled ? "16px" : "0px",
          paddingLeft: scrolled ? (scrolled ? "24px" : "0px") : "0px",
          paddingRight: scrolled ? (scrolled ? "24px" : "0px") : "0px",
        }}
        transition={{ type: "spring", stiffness: 220, damping: 30 }}
      >
        <motion.div
          className="mx-auto pointer-events-auto relative"
          style={{
            backdropFilter: scrolled ? "blur(40px) saturate(180%)" : "none",
            WebkitBackdropFilter: scrolled ? "blur(40px) saturate(180%)" : "none",
          }}
          animate={{
            maxWidth: scrolled ? "1240px" : "100%",
            backgroundColor: scrolled ? "rgba(255, 255, 255, 0.75)" : "transparent",
            height: scrolled ? "68px" : "88px",
            borderRadius: scrolled ? "40px" : "0px",
            borderWidth: scrolled ? "1px" : "0px",
            borderColor: scrolled ? "rgba(0,0,0,0.08)" : "transparent",
            boxShadow: scrolled ? "0 25px 50px -12px rgba(0,0,0,0.12)" : "none",
          }}
          transition={{ type: "spring", stiffness: 220, damping: 30, mass: 0.8 }}
        >
          <div className="flex items-center justify-between h-full w-full px-6 lg:px-10">
            {/* ── Logo ── */}
            <Link href="/" className="relative z-10 group">
              <motion.div
                animate={{ scale: scrolled ? 0.9 : 1 }}
                whileHover={{ scale: 1.05 }}
                className="relative w-[130px] h-[48px]"
              >
                <Image src="/logoo.svg" alt="Outcome Outing" fill priority className="object-contain" />
              </motion.div>
            </Link>

            {/* ── Desktop Nav ── */}
            <nav className="hidden xl:flex items-center gap-1 ml-auto mr-12">
              {navItems.map((item, idx) => {
                const active = isActive(item);
                const isHovered = hoveredIdx === idx;
                
                return (
                  <div 
                    key={idx} 
                    className="relative"
                    onMouseEnter={() => setHoveredIdx(idx)}
                    onMouseLeave={() => setHoveredIdx(null)}
                  >
                    <Link
                      href={item.href}
                      className={`
                        relative flex items-center h-10 px-4 rounded-full text-[15px] font-bold tracking-tight transition-all duration-500
                        ${active ? "text-white" : "text-[#1B1B1B]"}
                      `}
                    >
                      <div className="relative overflow-hidden flex items-center justify-center">
                        <motion.span
                          animate={{ y: isHovered && !active ? "-120%" : "0%" }}
                          className="block whitespace-nowrap"
                        >
                          {item.text}
                        </motion.span>
                        {!active && (
                          <motion.span
                            initial={{ y: "120%" }}
                            animate={{ y: isHovered ? "0%" : "120%" }}
                            className="absolute block text-[#FFAE00] whitespace-nowrap"
                          >
                            {item.text}
                          </motion.span>
                        )}
                      </div>

                      {item.hasDropdown && (
                        <ChevronDown size={14} className={`ml-1.5 transition-opacity ${active ? "opacity-100" : "opacity-40"}`} />
                      )}

                      {active && (
                        <motion.div 
                          layoutId="active-bg"
                          className="absolute inset-0 bg-[#FFAE00] rounded-full -z-10 shadow-lg shadow-[#FFAE00]/20"
                          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                        />
                      )}
                    </Link>

                    {/* Dropdown */}
                    <AnimatePresence>
                      {item.hasDropdown && isHovered && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 origin-top"
                        >
                          <div className="w-60 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-3">
                            {item.dropdownItems.map((drop, dIdx) => (
                              <Link
                                key={dIdx}
                                href={drop.href}
                                className="block px-5 py-3.5 rounded-xl hover:bg-[#FFAE00]/10 transition-colors group/drop"
                              >
                                <p className="text-[15px] font-bold text-gray-900 group-hover/drop:text-[#FFAE00]">{drop.text}</p>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </nav>

            {/* ── Actions ── */}
            <div className="hidden xl:flex items-center gap-3">
              <Link
                href="https://www.instagram.com/outcome_outing"
                target="_blank"
                className="group relative w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888] p-[1.5px] transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95"
              >
                <div className="w-full h-full bg-white rounded-full flex items-center justify-center transition-colors group-hover:bg-opacity-95">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:rotate-6">
                    <defs>
                      <linearGradient id="insta-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f09433" />
                        <stop offset="30%" stopColor="#e6683c" />
                        <stop offset="50%" stopColor="#dc2743" />
                        <stop offset="70%" stopColor="#cc2366" />
                        <stop offset="100%" stopColor="#bc1888" />
                      </linearGradient>
                    </defs>
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="url(#insta-grad)" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="url(#insta-grad)" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="url(#insta-grad)" />
                  </svg>
                </div>
              </Link>

              <motion.button
                onClick={() => window.open("https://invite.outcomeouting.com/founders", "_blank")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="
                  px-6 py-2.5 rounded-full bg-[#FFAE00] text-white font-bold text-[15px] tracking-tight
                  shadow-lg shadow-[#FFAE00]/20 transition-all hover:bg-[#E69D00] hover:shadow-[#FFAE00]/30
                  flex items-center gap-2 whitespace-nowrap
                "
              >
                Request Invite
                <ArrowUpRight size={16} />
              </motion.button>
            </div>

            {/* ── Mobile Trigger ── */}
            <button 
              onClick={() => setMenuOpen(true)}
              className="xl:hidden flex items-center justify-center w-11 h-11 rounded-full bg-black/5 hover:bg-black/10 transition-colors"
            >
              <div className="flex flex-col gap-1.5 h-full items-center justify-center">
                <div className="w-6 h-[1.5px] bg-black rounded-full" />
                <div className="w-5 h-[1.5px] bg-black rounded-full ml-auto" />
              </div>
            </button>
          </div>
        </motion.div>
      </motion.header>

      {/* ── Awwwards Mobile Menu ── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black text-white h-screen flex flex-col"
          >
            <div className="flex justify-between items-center px-8 h-[90px]">
              <div className="relative w-[140px] h-[52px] invert brightness-0">
                <Image src="/logoo.svg" alt="Outcome Outing" fill className="object-contain" />
              </div>
              <button onClick={() => setMenuOpen(false)} className="group flex items-center gap-2">
                <span className="text-xs font-bold opacity-50 group-hover:opacity-100 transition-opacity">CLOSE</span>
                <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <X size={20} />
                </div>
              </button>
            </div>

            <div className="flex-1 flex flex-col justify-center px-8 lg:px-20">
              <div className="flex flex-col gap-6">
                {navItems.map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 + idx * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col"
                  >
                    <div className="flex items-center gap-4">
                      <Link
                        href={item.hasDropdown ? "#" : item.href}
                        onClick={(e) => {
                          if (item.hasDropdown) {
                            e.preventDefault();
                            setMobileDropdownOpen(!mobileDropdownOpen);
                          } else {
                            setMenuOpen(false);
                          }
                        }}
                        className={`
                          group relative inline-block text-2xl lg:text-7xl leading-tight font-bold font-bricolage tracking-tighter transition-all duration-300
                          ${isActive(item) && !item.hasDropdown ? "text-[#FFAE00] border-b-2 border-[#FFAE00] pb-1" : "text-white hover:text-[#FFAE00]"}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          {item.text}
                          {item.hasDropdown && (
                            <ChevronDown size={20} className={`transition-transform duration-300 ${mobileDropdownOpen ? "rotate-180" : ""}`} />
                          )}
                        </div>
                      </Link>
                    </div>

                    <AnimatePresence>
                      {item.hasDropdown && mobileDropdownOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="flex flex-col gap-4 mt-4 ml-4 overflow-hidden"
                        >
                          {item.dropdownItems.map((sub, sIdx) => (
                            <Link
                              key={sIdx}
                              href={sub.href}
                              onClick={() => setMenuOpen(false)}
                              className={`text-xl font-bold font-jakarta tracking-tight transition-colors ${pathname === sub.href ? "text-[#FFAE00]" : "text-gray-400 hover:text-white"}`}
                            >
                              {sub.text}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="p-8 lg:p-20 border-t border-white/10 flex flex-col lg:flex-row justify-between items-center lg:items-center gap-8">
              <div className="flex gap-4">
                <Link
                  href="https://www.instagram.com/outcome_outing"
                  target="_blank"
                  className="group relative w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888] p-[1.5px] transition-all active:scale-95"
                >
                  <div className="w-full h-full bg-black rounded-full flex items-center justify-center transition-colors group-hover:bg-white/5">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:rotate-6">
                      <defs>
                        <linearGradient id="insta-grad-drawer" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#f09433" />
                          <stop offset="100%" stopColor="#bc1888" />
                        </linearGradient>
                      </defs>
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="url(#insta-grad-drawer)" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="url(#insta-grad-drawer)" />
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="url(#insta-grad-drawer)" />
                    </svg>
                  </div>
                </Link>
              </div>
              <button 
                onClick={() => window.open("https://invite.outcomeouting.com/founders", "_blank")}
                className="w-full lg:w-auto px-10 py-5 rounded-full bg-[#FFAE00] text-black font-bold text-lg hover:scale-105 active:scale-95 transition-transform"
              >
                Request Invite
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer */}
      <div className="h-[90px] pointer-events-none" />
    </>
  );
};

export default Header;
